<template>
  <div class="empty-state" :class="{ 'state-bordered': bordered }" :data-testid="sectionTag" v-bind="$attrs">
    <div class="empty-state__icon">
      <v-icon
        :data-testid="sectionTag ? `${sectionTag}-icon` : undefined"
        :icon="props.icon"
        :color="props.iconColor"
        :size="props.iconSize"
      ></v-icon>
    </div>
    <h4
      v-if="props.headline"
      v-dompurify-html="props.headline"
      :data-testid="sectionTag ? `${sectionTag}-headline` : undefined"
      class="text-center"
    ></h4>
    <div v-if="props.body" class="empty-state__body">
      <span v-dompurify-html="props.body" :data-testid="sectionTag ? `${sectionTag}-body` : undefined" class="body-regular-large"></span>
    </div>
    <div v-if="props.linkUrl && props.linkText" class="empty-state__link">
      <a :data-testid="sectionTag ? `${sectionTag}-link` : undefined" :href="props.linkUrl">{{
        props.linkText
      }}</a>
    </div>
    <div v-if="props.buttonText || $slots.actions" class="empty-state__button">
      <slot name="actions">
        <WButton
          :section-tag="sectionTag ? `${sectionTag}-button` : undefined"
          :variant="props.buttonVariant"
          :icon="props.buttonIcon"
          :label="buttonText"
          @click="buttonClick"
        >
        </WButton>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { colors } from '@/plugins/vuetify'
import { WButton } from '@wasabi/actions'

export interface EmptyStateProps {
  sectionTag?: string
  state?: string
  icon: string
  iconColor?: string
  iconSize?: number
  headline?: string
  body?: string
  linkText?: string
  linkUrl?: string
  buttonText?: string
  buttonIcon?: string
  buttonVariant?: string
  bordered?: boolean
  maxWidth?: string
}

const props = withDefaults(defineProps<EmptyStateProps>(), {
  sectionTag: 'unspecified',
  state: 'empty',
  iconColor: 'neutral800',
  iconSize: 50,
  headline: '',
  body: '',
  linkText: '',
  linkUrl: '',
  buttonText: '',
  buttonIcon: '',
  buttonVariant: 'secondary',
  bordered: false,
  maxWidth: '520px'
})

const emit = defineEmits(['buttonClick'])

const buttonClick = () => {
  emit('buttonClick')
}
</script>

<style lang="scss">
.empty-state {
  display: flex;
  align-items: center;
  max-width: v-bind('props.maxWidth');
  margin: 0 auto;
  flex-direction: column;
  padding-top: 90px;
  padding-bottom: 90px;

  &.state-bordered {
    border: 1px solid v-bind('colors.neutral.neutral300');
    border-radius: 4px;
    max-width: 100%;
  }
}

.empty-state__icon {
  margin-bottom: 20px;
  .v-icon {
    width: v-bind('props.iconSize');
    height: v-bind('props.iconSize');
  }
}

.empty-state__body {
  margin-top: 10px;
  text-align: center;
  padding: 0px 20px;
  white-space: normal;
}

.empty-state__button {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--spacing-gap-small);
  margin-top: 45px;
}

.empty-state__link,
.empty-state__body {
  a {
    color: v-bind('colors.blue.blue600') !important;
    text-decoration: underline;
  }
}
</style>
